import { Fragment } from 'react';
import { useFormikContext } from 'formik';
import BN from 'bn.js';

import SendTabs from '../send-tabs/send-tabs';
import { Button } from 'components/common/button/button';
import SendToField from 'components/common/form/send-to-field';
import {
  StyledInputMount,
  StyledButtonWrapper,
  StyledInputWrapper,
  StyledInputLabel,
  StyledInputValue,
  StyledTitle,
  StyledDescription,
} from '../../increase-storage-view.style';

import useIsSuccessAndError from 'lib/hooks/use-is-success-and-error';
import { useAppSelector } from 'redux/use-hooks';
import { selectActiveToken } from 'redux/slices/tokens';
import { selectStorageBalance } from 'redux/slices/account';
import { storageTabsOptions } from 'lib/constants';
import { getFontSize, getFieldProps, isValidAmount } from 'utils';
import { FormValues } from 'types';
import FungibleTokens from 'services/fungible-tokens';
import config from 'config';

const { OCC_CONTRACT } = config;

const { getFormattedTokenAmount } = FungibleTokens;

const EnterAmountTabs = ({ activeTab }) => {
  const formik = useFormikContext<FormValues>();
  const { values, setFieldValue } = formik;
  const { isLoading, isError } = useIsSuccessAndError();
  const nearTokenBalance = useAppSelector(selectActiveToken);
  const storageBalance = useAppSelector(selectStorageBalance);
  const occStorageBalance = storageBalance[OCC_CONTRACT];

  const [myStorageTab, sendByIdTab] = storageTabsOptions;
  const isMyStorageTab = activeTab.value === myStorageTab.value;
  const isSendByIdTab = activeTab.value === sendByIdTab.value;

  const handleUseMax = () => {
    const formattedTokenAmount = getFormattedTokenAmount(
      nearTokenBalance.balance,
      nearTokenBalance.onChainFTMetadata.symbol
    );
    if (!new BN(nearTokenBalance.balance).isZero()) {
      setFieldValue('amount', formattedTokenAmount.replace(/,/g, ''));
    }
  }

  const isDisabled = () => {
    if (isSendByIdTab && (isLoading || isError)) return true;
    if (!(isValidAmount(formik.values.amount) && formik.isValid)) return true;
    return false;
  };

  return (
    <Fragment>
      <StyledInputMount
        required
        autoFocus
        style={{
          fontSize: `${
            values.amount.length ? getFontSize(values.amount.length) : 64
          }px`
        }}
        {...getFieldProps(formik, 'amount')}
      />
      <StyledButtonWrapper>
        <Button
          type="button"
          variant="secondary"
          size="small"
          onClick={() => handleUseMax()}
        >
          Use Max
        </Button>
      </StyledButtonWrapper>
      {isMyStorageTab && (
        <StyledInputWrapper>
          <StyledInputLabel>Storage Deposit</StyledInputLabel>
          <StyledInputValue>
            {getFormattedTokenAmount(
              occStorageBalance.available,
              nearTokenBalance.onChainFTMetadata.symbol
            )}
          </StyledInputValue>
        </StyledInputWrapper>
      )}
      {isSendByIdTab && (
        <Fragment>
          <SendToField mb='0px' label="Send to" />
          <StyledDescription>
            The account ID must a Top Level account such as <b>.near</b> or contain exactly 64 characters
          </StyledDescription>
        </Fragment>
      )}
      <StyledInputWrapper>
        <StyledInputLabel>Available to Send</StyledInputLabel>
        <StyledInputValue>
          {getFormattedTokenAmount(
            nearTokenBalance.balance,
            nearTokenBalance.onChainFTMetadata.symbol
          )}
        </StyledInputValue>
      </StyledInputWrapper>
      <Button type="submit" disabled={isDisabled()} fullwidth>
        Confirm
      </Button>
    </Fragment>
  )
};

const EnterAmountView = ({ activeTab, setActiveTab }) => {
  return (
    <Fragment>
      <StyledTitle>Increase Storage</StyledTitle>
      <SendTabs tab={activeTab} onSelect={setActiveTab} />
      <EnterAmountTabs activeTab={activeTab} />
    </Fragment>
  )
};

export default EnterAmountView;
